import {createSpriteAtlasPruner} from '../lib/sprite-atlas.js';
import { is3DView } from '../lib/camera-state.js';
import { isMapStyleReady } from '../mapcore.js';
// Un solo layer per 2D e 3D: ogni stazione filtrata conserva il proprio
// marker completo e la coordinata originale, senza declutter o clustering.
import {
    stationDisplayRecord, rankExtremeRecords, stationNetworkFullLabel, isStaleStation,
} from '../lib/params.js';
import { getColorScale, rainIntervalColor } from '../lib/scales.js';
import {
    buildStationSprite, stationSpriteKey, markerZoomMode, stationSpriteDpr,
    stationHasWindVane, setMarkerSizeOverride,
} from './station-sprites.js';
import { isMobile } from '../lib/config.js';
import { temperatureExtreme } from '../lib/temperature-extreme.js';

const SRC = 'ws-stations';
const LYR = 'ws-stations-symbols';
const RAIN_SRC = 'ws-stations-rain';
const RAIN_LYR = 'ws-stations-rain-halo';
// GeoJSON-vt quantizza le coordinate alla griglia della tile. Con maxzoom 4
// lo scarto a latitudini siciliane puo superare i 100 m; z14 conserva invece
// la posizione anagrafica con precisione sub-metrica, con un costo irrilevante
// per il numero di stazioni gestito qui.
const STATION_SOURCE_MAXZOOM = 14;
const MAX_ATLAS_IMAGES = 700; // oltre: prune delle immagini non più usate
const SELECTABLE_NETWORKS = new Set([
    'rainwatch', 'unipa', 'cnr',
    'sias', 'drpc', 'proag', 'ws', 'meteosicilia', 'meteomessina', 'meteolicata', 'cms', 'cisar_ag', 'inaf', 'bess', 'meteocaltanissetta', 'metar', 'mareografica',
]);
// ARPACAL resta un riferimento nella vista predefinita completa, ma non deve
// sopravvivere a un filtro esplicito: "solo SIAS" deve mostrare davvero SIAS.
const REFERENCE_NETWORKS = new Set(['arpacal']);

let botScreenshot = false;
try { botScreenshot = localStorage.getItem('ws_bot_screenshot') === '1'; } catch {}

export const state = {
    map: null,
    parametro: 'temperatura',
    // Default v1: vento OFF, quota ON, estremi ON (index.php v1).
    options: { wind: false, altitudeLabels: true, extremes: true, instantLabels: true, temperatureExtremes: false, visible: true },
    hideZeroRain: false, // pioggia: di default mostra anche gli 0; toggle/bot lo attiva
    // Filtri v1: quota/valore dual-range + provincia (point-in-polygon sui
    // poligoni comunali, come wsIsRowInActiveProvince).
    filters: { altMin: null, altMax: null, valMin: null, valMax: null, province: null, municipality: null },
    networks: new Set(SELECTABLE_NETWORKS),
    data: [],
    records: [],
    // Record filtrati per pioggia/quota/valore MA senza il filtro geografico
    // (provincia/comune): li usano i riquadri-isola, che devono mostrare le
    // stazioni dell'isola anche quando la mappa principale è ristretta a una
    // provincia (es. Agrigento → Pelagie restano visibili nel box).
    recordsAll: [],
    visible: [],
    requestedStationId: '',
    botMode: false,        // ?bot=1: TUTTE le stazioni, niente declutter (screenshot statici)
    embedStationId: '',    // ?embed=1&station=ID: SOLO quella stazione (v1 filterEmbedStationRecords)
    _images: new Set(),
    _timer: null,
    _debounce: null,
    _fetching: false,
    _fetchPending: false,
    _dataVersion: 0,
    _appliedDataVersion: -1,
    _visibilityWired: false,
    _baseData: [],
    _rapidTimer: null,
    _rapidActive: false,
    _rapidSignature: '',
    _recentRain: { hours: 0, at: 0, meta: null, values: new Map(), request: 0 },
};

async function refreshRecentRain(hours) {
    const cache = state._recentRain;
    if (cache.hours === hours && Date.now() - cache.at < 60000) return;
    const request = ++cache.request;
    try {
        const payload = await window.WSWeatherApi.getRainAccumulation({ hours });
        if (request !== cache.request || state.parametro !== `rain${hours}h`) return;
        cache.hours = hours;
        cache.at = Date.now();
        cache.meta = payload.meta;
        cache.values = new Map((payload.points || []).map(point => [String(point.id), point.rain_mm]));
        rebuildRecords();
    } catch (error) {
        if (request === cache.request && state.parametro === `rain${hours}h`) {
            cache.values = new Map();
            cache.meta = null;
            cache.at = Date.now();
            console.warn('[wsv2] accumuli ultime ore non disponibili', error);
            rebuildRecords();
        }
    }
}

// --- sprite → immagine MapLibre (ancora ricentrata: il canvas viene esteso
//     simmetricamente attorno al punto d'ancora, così icon-anchor:'center'
//     posiziona l'ancora esattamente sulla coordinata) -----------------------
// PERF (bug "a chiazze" 2026-07-03): canvas d'appoggio UNICO riusato e
// willReadFrequently=true (CPU-backed): il getImageData per-sprite non forza
// più un round-trip GPU → costruire ~570 sprite passa da secondi a centinaia
// di ms (misurato). L'ImageData è copiato da addImage: il riuso è sicuro.
let _scratch = null;
function centeredImage(sprite) {
    const { canvas, w, h, ax, ay, dpr } = sprite;
    const W = 2 * Math.max(ax, w - ax);
    const H = 2 * Math.max(ay, h - ay);
    if (!_scratch) _scratch = document.createElement('canvas');
    const c = _scratch;
    const pw = Math.ceil(W * dpr);
    const ph = Math.ceil(H * dpr);
    if (c.width < pw) c.width = pw;
    if (c.height < ph) c.height = ph;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.clearRect(0, 0, pw, ph);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.drawImage(canvas, W / 2 - ax, H / 2 - ay, w, h);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    return { data: ctx.getImageData(0, 0, pw, ph), pixelRatio: dpr };
}

function spriteOpts(rec) {
    return {
        readable: is3DView(state.map),
        windEnabled: state.options.wind || (state.parametro === 'vento' && !state.botMode && !botScreenshot),
        windArrows: state.botMode || botScreenshot,
        barbValues: state.parametro === 'vento' && !state.botMode && !botScreenshot,
        gustDirection: state.botMode || botScreenshot,
        mapBearing: state.map?.getBearing() || 0,
        altitudeLabel: state.options.altitudeLabels ? stationAltitudeLabel(rec.station) : '',
        badgesVisible: state.options.extremes,
        instantVisible: state.options.instantLabels && !state.botMode && !botScreenshot,
        temperatureExtreme: state.options.temperatureExtremes && state.parametro === 'temperatura'
            && !snapshotMode && !isStaleStation(rec.station)
            && rec.station.age_minutes != null && Number(rec.station.age_minutes) <= 15
            ? temperatureExtreme(rec.station) : '',
    };
}

// Port di stationAltitudeLabel/stationAltitudeValue (map-legend.js): quota in m.
function stationAltitudeValue(row) {
    const raw = row?.quota ?? row?.altitudine ?? row?.altitude ?? row?.elevation ?? row?.alt ?? null;
    const n = parseFloat(raw);
    return Number.isFinite(n) ? n : null;
}
function stationAltitudeLabel(row) {
    const alt = stationAltitudeValue(row);
    if (alt === null) return '';
    return `${Math.round(alt)} m`;
}

function ensureLayer() {
    const map = state.map;
    if (map.getSource(SRC)) return;
    // Il source deve mantenere la precisione delle coordinate anagrafiche.
    // La stabilita visiva resta garantita dal setData atomico e dagli sprite
    // preparati prima del publish, senza degradare la geometria a una tile z4.
    map.addSource(SRC, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
        maxzoom: STATION_SOURCE_MAXZOOM, buffer: 0, tolerance: 0,
    });
    // Alone pioggia: un GLOW blu tenue SOTTO i marker, sulle stazioni che stanno
    // prendendo pioggia (giornaliera > 0). Visibile anche in "Temperatura" per
    // individuarle a colpo d'occhio. Circle layer = leggerissimo (GPU, niente
    // symbol-placement) → nessun costo sullo zoom.
    map.addSource(RAIN_SRC, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
        maxzoom: STATION_SOURCE_MAXZOOM,
        buffer: 0,
        tolerance: 0,
    });
    map.addLayer({
        id: RAIN_LYR,
        type: 'circle',
        source: RAIN_SRC,
        paint: {
            // Raggio grande anche a bassa scala: da lontano i marker si
            // sovrappongono e coprivano l'alone → ora forma macchie blu visibili
            // sopra le aree che stanno prendendo pioggia (percettibile da lontano),
            // e circonda i singoli marker quando zoomi.
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 18, 7, 22, 9, 28, 11, 36, 13, 46],
            // The pulse already interpolates in time. Default 300 ms style
            // transitions otherwise keep the entire terrain repainting at 120 Hz.
            'circle-radius-transition': {duration: 0, delay: 0},
            'circle-opacity-transition': {duration: 0, delay: 0},
            'circle-stroke-opacity-transition': {duration: 0, delay: 0},
            'circle-color': 'rgba(56, 132, 255, 0.38)',
            'circle-blur': 0.45,
            'circle-stroke-width': 1.6,
            'circle-stroke-color': 'rgba(56, 132, 255, 0.78)',
        },
    });
    map.addLayer({
        id: LYR,
        type: 'symbol',
        source: SRC,
        layout: {
            'icon-image': ['get', 'sk'],
            'icon-pitch-alignment': 'viewport',
            'icon-rotation-alignment': 'viewport',
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
            // Impilamento come i divIcon DOM: chi sta più in basso sullo
            // schermo copre chi sta sopra (stesso ordine visivo della v1).
            'symbol-z-order': 'viewport-y',
            'symbol-sort-key': ['get', 'y'],
        },
    });
}

// Timeline nel FUTURO: le stazioni si nascondono (si vedono le previsioni),
// come in v1. Non tocca la preferenza chkMarkers dell'utente.
let futureHidden = false;
export function isFutureHidden() { return futureHidden; }
export function setFutureHidden(on) {
    if (futureHidden === !!on) return;
    futureHidden = !!on;
    render();
    // I riquadri-isola NON passano da render(): avvisali così anche loro nascondono
    // (nel futuro) o rimostrano (tornando a ora/passato) i pallini live — prima il
    // marker temperatura live restava appiccicato nell'inset (bug 2026-07-05).
    document.dispatchEvent(new CustomEvent('wsv2:stations-mode'));
}

// Soppressione TEMPORANEA dei marker parametro quando è attivo un CAMPO ANALISI a
// punti (Pioggia cumulata / Variazione temperatura): quei layer disegnano già un
// cerchio per-stazione, i marker parametro sotto sono solo confusione (richiesta
// Giuseppe 2026-07-07). NON tocca la preferenza utente (chkMarkers/state.options.
// visible): allo spegnimento del campo i marker tornano com'erano.
let fieldSuppressed = false;
export function isStationsFieldSuppressed() { return fieldSuppressed; }
export function setStationsFieldSuppressed(on) {
    if (fieldSuppressed === !!on) return;
    fieldSuppressed = !!on;
    render();
    document.dispatchEvent(new CustomEvent('wsv2:stations-mode'));
}

// Pulsazione MOLTO leggera dell'alone (respiro d'opacità) → segnala "sta
// piovendo" senza essere invasiva. rAF con update ~18fps (55ms), si ferma da
// sé quando non ci sono aloni o la tab è nascosta (nessun costo a riposo).
let _haloRaf = 0;
let _haloLast = 0;
function haloPulseTick(ts) {
    const map = state.map;
    if (!state._haloOn || document.hidden || !map || !map.getLayer(RAIN_LYR)) { _haloRaf = 0; return; }
    // In 3D, keep halos static: paint mutations invalidate terrain rendering.
    if (!is3DView(map) && !map.isMoving() && ts - _haloLast >= 55) {
        _haloLast = ts;
        const s = 0.5 + 0.5 * Math.sin(ts / 1000);   // 0..1 (periodo ~2π s)
        const k = 0.86 + 0.28 * s;                    // raggio "respira" ±14%
        const op = 0.6 + 0.4 * s;                     // opacità 0.6→1.0
        try {
            // raggio modulato → il respiro si VEDE (l'opacità sola era impercettibile)
            map.setPaintProperty(RAIN_LYR, 'circle-radius',
                ['interpolate', ['linear'], ['zoom'], 5, 18 * k, 7, 22 * k, 9, 28 * k, 11, 36 * k, 13, 46 * k]);
            map.setPaintProperty(RAIN_LYR, 'circle-opacity', op);
            map.setPaintProperty(RAIN_LYR, 'circle-stroke-opacity', Math.min(1, op + 0.12));
        } catch (e) {}
    }
    _haloRaf = requestAnimationFrame(haloPulseTick);
}
function startHaloPulse() { if (!_haloRaf) _haloRaf = requestAnimationFrame(haloPulseTick); }

// Alone pioggia: sulle stazioni dove sta piovendo ORA (rain_rate > 0), NON dove
// ha già piovuto (accumulo giornaliero). Nei parametri pioggia/intensità sarebbe
// ridondante → nascosto.
function updateRainHalo() {
    const map = state.map;
    const src = map?.getSource(RAIN_SRC);
    if (!src) return;
    const isRainParam = RAIN_ACCUM_PARAMS.has(state.parametro) || state.parametro === 'rainrate';
    if (isRainParam) { src.setData({ type: 'FeatureCollection', features: [] }); state._haloOn = false; return; }
    const feats = [];
    for (const rec of state.visible) {
        const r = Number(rec.station?.rain_rate);
        if (Number.isFinite(r) && r > 0) {
            feats.push({ type: 'Feature', geometry: { type: 'Point', coordinates: [rec.lon, rec.lat] }, properties: {} });
        }
    }
    src.setData({ type: 'FeatureCollection', features: feats });
    state._haloOn = feats.length > 0;
    if (state._haloOn) startHaloPulse();
}

function render() {
    const map = state.map;
    if (!map) return;
    if (!isMapStyleReady(map)) {
        // Style non ancora pronto (primo load): riprova quando la mappa è idle,
        // altrimenti il primo batch di dati andrebbe perso fino al timer dei 60s.
        if (!state._pendingIdle) {
            state._pendingIdle = true;
            map.once('idle', () => { state._pendingIdle = false; render(); });
        }
        return;
    }
    ensureLayer();
    // Every eligible station remains visible at regional zoom, including wind.
    for (const property of ['icon-allow-overlap', 'icon-ignore-placement']) {
        if (map.getLayoutProperty(LYR, property) !== true) map.setLayoutProperty(LYR, property, true);
    }
    if (!state.options.visible || futureHidden || fieldSuppressed) {
        // Invalida anche un eventuale batch sprite avviato prima del toggle:
        // su device lenti non deve poter ripopolare la source dopo lo spegnimento.
        state._spriteGen = (state._spriteGen || 0) + 1;
        map.getSource(SRC).setData({ type: 'FeatureCollection', features: [] });
        map.getSource(RAIN_SRC)?.setData({ type: 'FeatureCollection', features: [] });
        state._haloOn = false;
        // BUG 2026-07-03: senza azzerare il diff-map, alla riaccensione il
        // diff contro _applied risultava vuoto → updateData mai chiamata →
        // source vuota per sempre. Azzerato: il prossimo render fa setData pieno.
        state._applied = null;
        return;
    }
    const mode = markerZoomMode(map.getZoom(), isMobile());
    state._lastBand = mode;
    // "Mostra direzione": la bussola SOSTITUISCE il marker — le stazioni senza
    // dato vento NON vengono renderizzate (v1 renderDomStationMarkers).
    const renderable = state.options.wind && state.parametro !== 'raffica'
        ? state.records.filter(rec => stationHasWindVane(rec.station, state.parametro))
        : state.records;
    state.visible = renderable;
    state._byId = new Map(state.visible.map(r => [r.id, r])); // lookup O(1) per l'hit-test click/hover
    updateRainHalo();

    const features = [];
    const used = new Set();
    const missing = [];
    for (const rec of state.visible) {
        const opts = spriteOpts(rec);
        const key = 'st|' + stationSpriteDpr() + '|' + (opts.readable ? '3d|' : '') + stationSpriteKey(rec, state.parametro, mode, opts);
        used.add(key);
        if (!map.hasImage(key)) missing.push({ rec, key, opts });
        const fid = Number(rec.id);
        features.push({
            type: 'Feature',
            id: Number.isFinite(fid) ? fid : Math.abs([...String(rec.id)].reduce((a, c) => a * 31 + c.charCodeAt(0) | 0, 7)),
            geometry: { type: 'Point', coordinates: [rec.lon, rec.lat] },
            properties: { sk: key, id: rec.id, y: rec.lat * -1 },
        });
    }
    // Le icone mancanti si costruiscono prima (blocchi ~8ms, marker vecchi
    // visibili), poi il payload viene applicato in modo atomico. I cambi UI
    // possono usare il diff; ogni nuova risposta live riallinea l'intera source.
    state._spriteGen = (state._spriteGen || 0) + 1;
    const gen = state._spriteGen;
    const prev = state._applied; // Map fid → sk dell'ultimo apply
    const dataVersion = state._dataVersion;
    const apply = () => {
        const src = map.getSource(SRC);
        if (!src) return;
        map.setLayoutProperty(LYR,'icon-rotation-alignment',state.options.wind?'map':'viewport');
        // Un nuovo payload live usa setData completo. Il diff incrementale resta
        // per i soli cambi UI (parametro/filtri/zoom), ma non e' piu' un punto
        // singolo di fallimento per il realtime: su alcune sessioni MapLibre
        // conservava la vecchia proprieta' `sk` e quindi il vecchio sprite.
        if (dataVersion !== state._appliedDataVersion) {
            src.setData({ type: 'FeatureCollection', features });
        } else if (prev && typeof src.updateData === 'function') {
            // Diff completo per i cambi puramente locali di UI.
            const nextIds = new Set(features.map(f => f.id));
            const remove = [...prev.keys()].filter(id => !nextIds.has(id));
            const add = features.filter(f => !prev.has(f.id));
            const update = features
                .filter(f => prev.has(f.id) && prev.get(f.id) !== f.properties.sk)
                .map(f => ({ id: f.id, addOrUpdateProperties: [{ key: 'sk', value: f.properties.sk }] }));
            if (add.length || remove.length || update.length) src.updateData({ add, remove, update });
        } else {
            src.setData({ type: 'FeatureCollection', features });
        }
        state._applied = new Map(features.map(f => [f.id, f.properties.sk]));
        state._appliedDataVersion = dataVersion;
        state._usedSpriteKeys = used;
        state._appliedSpriteGen = gen;
        state._pruneAtlas?.();
    };
    // BUG "a chiazze" (2026-07-03): al boot si faceva subito apply() con le
    // icone ancora da costruire → MapLibre disegnava i simboli man mano che
    // gli sprite arrivavano (gruppi sparsi per ~12s). Ora TUTTI gli sprite del
    // batch vengono rasterizzati PRIMA del setData: un solo apply atomico, i
    // marker compaiono in un colpo. Con centeredImage CPU-backed il batch
    // intero costa poche centinaia di ms (blocchi ~14ms: main thread vivo).
    if (!missing.length) { apply(); }
    else {
        // Budget per blocco: al BOOT (mappa ancora senza marker) blocchi più
        // grossi — non c'è niente da tenere fluido e su CPU lente i frame-gap
        // del rAF dominano il tempo totale; a regime blocchi corti.
        const budget = state._hasData ? 14 : 40;
        const step = () => {
            if (gen !== state._spriteGen) return; // superato da un render più nuovo
            const t0 = performance.now();
            while (missing.length && performance.now() - t0 < budget) {
                const m = missing.shift();
                if (map.hasImage(m.key)) continue;
                const sprite = buildStationSprite(m.rec, state.parametro, mode, m.opts);
                const img = centeredImage(sprite);
                map.addImage(m.key, img.data, { pixelRatio: img.pixelRatio });
                state._images.add(m.key);
            }
            if (missing.length) requestAnimationFrame(step);
            else apply();
        };
        step(); // primo blocco SUBITO (niente attesa del prossimo frame)
    }
    state._hasData = true;

    document.dispatchEvent(new CustomEvent('wsv2:stations-visible'));


}

// --- filtro provincia/comune: poligoni comunali (lazy, come v1) --------------
// Il GeoJSON ISTAT 2026 espone `province_code` (sigla, es. PA) e
// `municipality_name` (es. "Palermo"). ⚠️ Storicamente il loader leggeva
// `provincia`/`sigla` (assenti in questo dataset) → il filtro provincia era un
// no-op silenzioso: qui si legge la proprietà giusta e si indicizza anche il comune.
let provincePolys = null;      // sigla -> [{ring,bbox}]
let municipalityPolys = null;  // chiave-comune normalizzata -> [{ring,bbox}]
// chiave comune robusta: minuscolo, senza accenti, non-alfanumerici → spazio
// (matcha sia "Palermo" del GeoJSON sia comune=palermo / san-cataldo dell'URL).
// Export waits for the sharper sprite batch to be applied before reading the frame.
export function refreshStationSpriteResolution() {
    render();
    if (!state.map || !state.options.visible || futureHidden || fieldSuppressed) return Promise.resolve();
    return new Promise((resolve, reject) => {
        const deadline = performance.now() + 10000;
        const check = () => {
            if (state._appliedSpriteGen === state._spriteGen) return resolve();
            if (performance.now() > deadline) return reject(new Error('La mappa sta ancora caricando. Attendi qualche secondo e riprova.'));
            requestAnimationFrame(check);
        };
        check();
    });
}

export function municipalityKey(v) {
    return String(v || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '')        .toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}
async function ensureAdminPolys() {
    if (provincePolys && municipalityPolys) return;
    const resp = await fetch('/assets/data/sicily-municipalities.geojson', { cache: 'force-cache' });
    const gj = await resp.json();
    provincePolys = {}; municipalityPolys = {};
    for (const f of gj.features || []) {
        const sigla = String(f.properties?.province_code || '').toUpperCase();
        const mkey = municipalityKey(f.properties?.municipality_name);
        const polys = f.geometry.type === 'Polygon' ? [f.geometry.coordinates]
            : f.geometry.type === 'MultiPolygon' ? f.geometry.coordinates : [];
        for (const p of polys) {
            const ring = p[0];
            let minX = 999, minY = 999, maxX = -999, maxY = -999;
            for (const [x, y] of ring) { if (x < minX) minX = x; if (x > maxX) maxX = x; if (y < minY) minY = y; if (y > maxY) maxY = y; }
            const cell = { ring, bbox: [minX, minY, maxX, maxY] };
            if (sigla) (provincePolys[sigla] = provincePolys[sigla] || []).push(cell);
            if (mkey) (municipalityPolys[mkey] = municipalityPolys[mkey] || []).push(cell);
        }
    }
}
// Bounds [[w,s],[e,n]] del comune per l'inquadratura da deep-link (?comune=).
export async function municipalityBounds(name) {
    await ensureAdminPolys();
    const polys = municipalityPolys?.[municipalityKey(name)];
    if (!polys || !polys.length) return null;
    let w = 999, s = 999, e = -999, n = -999;
    for (const p of polys) { const [minX, minY, maxX, maxY] = p.bbox; if (minX < w) w = minX; if (minY < s) s = minY; if (maxX > e) e = maxX; if (maxY > n) n = maxY; }
    return [[w, s], [e, n]];
}
function inMunicipality(rec, key) {
    const polys = municipalityPolys?.[key];
    if (!polys) return true;
    for (const p of polys) {
        const [minX, minY, maxX, maxY] = p.bbox;
        if (rec.lon < minX || rec.lon > maxX || rec.lat < minY || rec.lat > maxY) continue;
        if (pointInRing(rec.lon, rec.lat, p.ring)) return true;
    }
    return false;
}
function pointInRing(x, y, ring) {
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const xi = ring[i][0], yi = ring[i][1], xj = ring[j][0], yj = ring[j][1];
        if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) inside = !inside;
    }
    return inside;
}
export function inProvince(rec, sigla) {
    const polys = provincePolys?.[sigla];
    if (!polys) return true;
    for (const p of polys) {
        const [minX, minY, maxX, maxY] = p.bbox;
        if (rec.lon < minX || rec.lon > maxX || rec.lat < minY || rec.lat > maxY) continue;
        if (pointInRing(rec.lon, rec.lat, p.ring)) return true;
    }
    return false;
}

// Parametri pioggia ad ACCUMULO: si mostrano SOLO le stazioni con accumulo
// REALE (raw > 0) — niente distesa di zeri (port 1:1 di
// stationPassesRainAccumFilter v1 map-markers.js, promossa a standard).
const RAIN_ACCUM_PARAMS = new Set(['rain', 'rainmonth', 'rainyear', 'rain1h', 'rain3h', 'rain6h', 'rain12h', 'rain24h', 'rain48h', 'rain72h', 'rain96h', 'rain120h']);
function passesRainAccumFilter(rec) {
    if (!RAIN_ACCUM_PARAMS.has(state.parametro)) return true;
    // Di DEFAULT si mostrano anche le stazioni a 0 (richiesta Giuseppe 2026-07-03);
    // il toggle "Nascondi stazioni senza pioggia" (o ?hidezerorain=1 usato dal bot)
    // le filtra via.
    if (!state.hideZeroRain) return true;
    const raw = Number(rec?.raw);
    return Number.isFinite(raw) && raw > 0;
}

// Filtri NON geografici (pioggia/quota/valore): valgono ovunque, inset compresi.
function passesBaseFilters(rec, f, includeDry = false) {
    if (!includeDry && !passesRainAccumFilter(rec)) return false;
    if (f.altMin != null || f.altMax != null) {
        const raw = rec.station.quota ?? rec.station.altitudine ?? rec.station.altitude ?? rec.station.elevation ?? rec.station.alt;
        const alt = parseFloat(raw);
        const a = Number.isFinite(alt) ? alt : 0;
        if (f.altMin != null && a < f.altMin) return false;
        if (f.altMax != null && a > f.altMax) return false;
    }
    if (f.valMin != null && rec.raw < f.valMin) return false;
    if (f.valMax != null && rec.raw > f.valMax) return false;
    return true;
}
// Filtro geografico (provincia/comune): ristringe SOLO la mappa principale, non
// gli inset isola.
function passesGeoFilters(rec, f) {
    if (f.province && !inProvince(rec, f.province)) return false;
    if (f.municipality && !inMunicipality(rec, f.municipality)) return false;
    return true;
}
function applyFilters(records, includeDry = false) {
    // Embed con stazione richiesta: si mostra SOLO quella (parità v1).
    if (state.embedStationId) records = records.filter(rec => rec.id === state.embedStationId);
    const f = state.filters;
    return records.filter(rec => passesBaseFilters(rec, f, includeDry) && passesGeoFilters(rec, f));
}

export function setFilters(patch) {
    Object.assign(state.filters, patch);
    const go = () => rebuildRecords();
    if ((state.filters.province || state.filters.municipality) && (!provincePolys || !municipalityPolys)) ensureAdminPolys().then(go).catch(go);
    else go();
}

const _colorCache = new Map(); // `${parametro}|${valore.toFixed(1)}` → colore
function rebuildRecords() {
    const parametro = state.parametro;
    const isRain = parametro === 'rain' || parametro === 'rainrate' || /^rain(?:1|3|6|12|24|48|72|96|120)h$/.test(parametro);
    const rawScale = isRain ? rainIntervalColor : getColorScale(parametro);
    // Le chiamate chroma dominano il costo su ~1200 record: i valori si
    // ripetono → cache per (parametro, valore a 0.1). Cap anti-crescita.
    if (_colorCache.size > 4000) _colorCache.clear();
    const scaleFn = (v) => {
        const k = parametro + '|' + (Math.round(v * 10) / 10);
        let c = _colorCache.get(k);
        if (c === undefined) { c = rawScale(v); _colorCache.set(k, c); }
        return c;
    };
    let records = state.data
        .map(s => /^rain(?:1|3|6|12|24|48|72|96|120)h$/.test(parametro)
            ? stationDisplayRecord({ ...s, pioggia_ultime_ore: state._recentRain.values.get(String(s.id)) ?? null }, parametro, scaleFn, isRain)
            : stationDisplayRecord(s, parametro, scaleFn, isRain))
        .filter(Boolean);
    // Insieme "senza filtro geografico" per gli inset isola: applica i soli
    // filtri base (pioggia/quota/valore) e l'eventuale embedStationId.
    const f = state.filters;
    let baseFiltered = state.embedStationId ? records.filter(r => r.id === state.embedStationId) : records;
    baseFiltered = baseFiltered.filter(rec => passesBaseFilters(rec, f));
    state.recordsAll = baseFiltered;
    // Hiding dry markers is presentation only: zero observations still constrain rain interpolation.
    const interpolationRecords = applyFilters(records, true);
    records = applyFilters(records);
    rankExtremeRecords(records, parametro);
    state.records = records;
    render();
    // Universo "online" per il badge (v1 stationOnlineUniverse): TUTTE le
    // stazioni fresche <2h a prescindere dal parametro, PIÙ le boe-only
    // fresche (es. ondametrica 2358 Mazara) che live conta ma i marker no.
    // Serve SOLO al conteggio, mai al rendering.
    const buoyOnly = Array.isArray(window.wsBuoyOnlyRows) ? window.wsBuoyOnlyRows : [];
    const online = state.data.filter(s => !isStaleStation(s)).length
        + buoyOnly.filter(s => !isStaleStation(s)).length;
    document.dispatchEvent(new CustomEvent('wsv2:stations', {
        detail: { records, interpolationRecords, stations: state.data, parametro, online },
    }));
}

// Timeline unificata: nel PASSATO le stazioni mostrano lo snapshot storico
// (drawStationSnapshotRows v1); a ORA si torna al live (e il timer riprende).
let snapshotMode = false;
export function setSnapshotRows(rows) {
    snapshotMode = true;
    state.data = Array.isArray(rows) ? rows : [];
    rebuildRecords();
}
export function resumeLive() {
    if (!snapshotMode) return;
    snapshotMode = false;
    fetchData();
}

function selectedNetworkFilters() {
    const selected = [...state.networks].filter(network => SELECTABLE_NETWORKS.has(network));
    const hasEverySelectableNetwork = selected.length === SELECTABLE_NETWORKS.size
        && [...SELECTABLE_NETWORKS].every(network => state.networks.has(network));
    return Array.from(new Set([
        ...selected,
        ...(hasEverySelectableNetwork ? REFERENCE_NETWORKS : []),
    ])).sort();
}

async function fetchData() {
    if (snapshotMode) return;
    if (state._fetching) {
        // Non perdere un cambio rete avvenuto mentre la richiesta precedente è
        // in volo: appena termina viene caricata la selezione più recente.
        state._fetchPending = true;
        return;
    }
    state._fetching = true;
    try {
        do {
            state._fetchPending = false;
            const filters = selectedNetworkFilters();
            const requestKey = filters.join(',');
            const rows = filters.length
                ? await window.WSWeatherApi.getStations({ filters })
                : [];

            // Una selezione più nuova rende obsoleto questo risultato: non
            // disegnarlo neppure per un frame, passa direttamente al filtro nuovo.
            if (state._fetchPending || requestKey !== selectedNetworkFilters().join(',')) {
                state._fetchPending = true;
                continue;
            }

            // Una risposta della sorgente ordinaria può terminare dopo quella
            // Instant (tipico tornando su una tab in background). Conserva
            // l'overlay rapido già attivo: il refresh a 60 s non deve mai far
            // regredire temporaneamente la stazione alla modalità normale.
            const rapidStations = state._rapidActive
                ? (state._rapidRows || [])
                : [];
            state._baseData = Array.isArray(rows) ? rows : [];
            state.data = state._baseData.slice();
            for (const rapidStation of rapidStations) {
                const rapidIndex = state.data.findIndex(row => String(row?.id ?? '') === String(rapidStation.id));
                if (rapidIndex >= 0) state.data[rapidIndex] = mergeRapidStation(state.data[rapidIndex], rapidStation);
            }
            state._dataVersion += 1;
            const recentHours = /^rain(1|3|6|12|24|48|72|96|120)h$/.exec(state.parametro);
            if (recentHours) refreshRecentRain(Number(recentHours[1]));
            rebuildRecords();
        } while (state._fetchPending && !snapshotMode);
    } catch (err) {
        console.warn('[wsv2] stazioni: fetch fallita', err);
    } finally {
        state._fetching = false;
        if (state._fetchPending && !snapshotMode) {
            state._fetchPending = false;
            queueMicrotask(fetchData);
        }
    }
}

function mergeRapidStation(base, rapid) {
    // Packet gusts are not daily maxima. Keep ordinary extrema only while fresh.
    const { wind_gust, raffica, wind_gust_direction, wind_gust_direction_degrees, ...live } = rapid;
    const merged = { ...base, ...live,
        time_utc: rapid.observed_at ?? rapid.time_utc ?? base.time_utc };
    const stale = base.is_stale === true || Number(base.is_stale) === 1 || Number(base.age_minutes) > 30;
    const periodKey = (stamp, length) => {
        if (!stamp) return null;
        const text = String(stamp);
        if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(text)) return /^\d{4}-\d{2}-\d{2}/.test(text) ? text.slice(0, length) : null;
        const date = new Date(text);
        if (!Number.isFinite(date.getTime())) return null;
        return new Intl.DateTimeFormat('sv-SE', {timeZone:'Europe/Rome',year:'numeric',month:'2-digit',day:'2-digit'}).format(date).slice(0, length);
    };
    const baseStamp = base.time_utc ?? base.observed_at ?? base.last_update;
    const rapidStamp = rapid.observed_at ?? rapid.time_utc ?? (rapid.observed_epoch ? new Date(rapid.observed_epoch*1000).toISOString() : null);
    const differentDay = periodKey(baseStamp,10) && periodKey(rapidStamp,10) && periodKey(baseStamp,10) !== periodKey(rapidStamp,10);
    const aliases = [
        ['pioggia', 'rain_day', 'dayRain'],
        ['pioggia_mensile', 'rain_month', 'monthRain'],
        ['pioggia_annuale', 'rain_year', 'yearRain'],
        ['dewPoint', 'dew_point'], ['windChill', 'wind_chill'],
        ['wetBulb', 'wet_bulb'], ['pressione', 'pressure'],
        ['rain_rate', 'rateRain'], ['temperatura', 'temperature'],
        ['umidita', 'humidity'], ['vento', 'wind'], ['direzione', 'wind_direction'],
    ];
    for (const keys of aliases) {
        const key = keys.find(key => live[key] !== undefined && live[key] !== null);
        const rainIndex = aliases.indexOf(keys);
        if (rainIndex < 3) {
            const length = [10,7,4][rainIndex];
            const oldPeriod = periodKey(baseStamp,length), newPeriod = periodKey(rapidStamp,length);
            const changedPeriod = oldPeriod && newPeriod && oldPeriod !== newPeriod;
            // Ordinary rain is quality-controlled and may contain corrections.
            // Never overwrite a current archive total with an unvalidated console counter.
            const value = base[keys[0]] == null ? null : (!stale && !changedPeriod ? base[keys[0]] : (key !== undefined ? live[key] : null));
            for (const alias of keys) merged[alias] = value ?? null;
        } else if (key !== undefined) for (const alias of keys) merged[alias] = live[key];
        else if (stale) for (const alias of keys) merged[alias] = null;
    }
    if (stale || differentDay) {
        for (const key of ['tmin','tmax','temperature_min','temperature_max','raffica','wind_gust',
            'oraTempMin','oraTempMax','wind_gust_time','wind_gust_direction','wind_gust_direction_degrees']) merged[key] = null;
    }
    if (stale) merged.comfort = { available: false };
    for (const key of ['temperatura','dewPoint','tmin','tmax'])
        merged['has_' + key] = merged[key] !== null && merged[key] !== undefined;
    return merged;
}

function applyRapidStation(stations) {
    const signature = JSON.stringify(stations);
    if (state._rapidActive && signature === state._rapidSignature) return;
    state._rapidRows = stations;
    const rows = state._baseData.slice();
    for (const station of stations) {
        const index = rows.findIndex(row => String(row?.id) === String(station.id));
        if (index >= 0) rows[index] = mergeRapidStation(rows[index], station);
    }
    state.data = rows;
    state._rapidActive = true;
    state._rapidSignature = signature;
    state._dataVersion += 1;
    rebuildRecords();
}

function restoreBaseStation() {
    if (!state._rapidActive) return;
    state._rapidActive = false;
    state._rapidSignature = '';
    state.data = state._baseData.slice();
    state._dataVersion += 1;
    rebuildRecords();
}

async function fetchRapidStation() {
    if (snapshotMode || document.hidden) return;
    try {
        const response = await fetch('/api/rapid-station.php?all=1', { cache: 'no-store', credentials: 'same-origin' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const payload = await response.json();
        if (payload?.stations?.length) applyRapidStation(payload.stations);
        else restoreBaseStation();
    } catch (_err) {
        restoreBaseStation();
    }
}

function scheduleRender() {
    if (state._debounce) clearTimeout(state._debounce);
    // Il pan NON ridisegna mai (source già completa). Si ri-renderizza SOLO
    // quando cambia la FASCIA zoom (low/mid/full → sprite diversi).
    state._debounce = setTimeout(() => {
        state._debounce = null;
        const band = markerZoomMode(state.map.getZoom(), isMobile());
        if (band !== state._lastBand) render();
    }, 110);
}

// --- tooltip hover (replica getStationTooltip v1) ---------------------------
const HHMMSS = new Intl.DateTimeFormat('it-IT', {
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: 'Europe/Rome',
});
function esc(s) {
    return String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
function tooltipHtml(rec) {
    const s = rec.station;
    const name = s.localita || s.location || s.comune || s.city || s.name || s.nome || 'Stazione';
    const ts = s.time_utc ?? s.last_update ?? s.updated_at ?? s.ora ?? s.data ?? null;
    let time = '';
    if (ts) { const d = new Date(ts); if (!isNaN(d)) time = HHMMSS.format(d); }
    const alt = stationAltitudeLabel(s);
    return `
<div class="ws-station-tooltip">
  <div class="ws-station-tooltip__top">
    <span class="ws-station-tooltip__badge">${esc(stationNetworkFullLabel(s))}</span>
    ${time ? `<span class="ws-station-tooltip__time">${esc(time)}</span>` : ''}
  </div>
  <div class="ws-station-tooltip__name">${esc(name)}</div>
  ${alt ? `<div class="ws-station-tooltip__altitude">${esc(alt)}</div>` : ''}
</div>`.trim();
}

// BUG "il click apre la stazione sbagliata / non si apre" (utenti, 2026-07-06):
// gli sprite sono quad grandi con MOLTO padding trasparente (badge sopra, quota
// sotto, freccia vento) e, col declutter disattivato, si sovrappongono a valanga
// (a z7 ~572/574 marker hanno >1 quad sotto lo stesso pixel). L'area interrogata
// da MapLibre è l'INTERO quad, quindi e.features[0] è la stazione il cui QUAD sta
// davanti — non quella il cui PALLINO visibile è sotto il cursore. Riprodotto
// headless: cliccando sull'ancora di una stazione, features[0] era un'altra nel
// 98% dei casi a z7. Fix: tra tutti i candidati sotto il punto, scegliamo quello
// il cui ANCORA proiettata (il pallino che l'utente vede) è più vicino al click;
// a parità di distanza vince chi è renderizzato SOPRA (viewport-y: lat minore,
// più in basso sullo schermo) per coerenza con ciò che si vede.
// Raggio (px) entro cui due stazioni sono "impilate" e vanno offerte a scelta.
const CLUSTER_RADIUS_PX = 16;

// Ritorna { rec, cluster }: rec = la stazione col pallino più vicino al click
// (quella disegnata sopra a parità), cluster = TUTTE le stazioni impilate sotto
// il dito (entro CLUSTER_RADIUS_PX), ordinate primo=rec. Serve al picker: quando
// due stazioni cadono sullo stesso pixel (Nicito/Ughetti, ~100m → 0px sotto z11)
// nessun click può raggiungere quella sotto → si offre la scelta invece di
// costringere a zoomare (bug utenti 2026-07-06, secondo giro).
function pickCluster(e) {
    const feats = e.features;
    if (!feats || !feats.length) return { rec: null, cluster: [] };
    const map = state.map;
    const px = e.point.x, py = e.point.y;
    const seen = new Map(); // id -> { rec, d } (dedup: e.features ripete lo stesso quad)
    for (const f of feats) {
        const id = f.properties.id;
        if (seen.has(id)) continue;
        const rec = state._byId ? state._byId.get(id) : state.visible.find(r => r.id === id);
        if (!rec) continue;
        const p = map.project([rec.lon, rec.lat]);
        seen.set(id, { rec, d: Math.hypot(p.x - px, p.y - py) });
    }
    // Ordine: per distanza dal click; a parità (<3px) vince chi è disegnato SOPRA
    // (viewport-y: lat più bassa = più in basso sullo schermo = sopra).
    const arr = [...seen.values()].sort((a, b) =>
        Math.abs(a.d - b.d) <= 3 ? (a.rec.lat - b.rec.lat) : (a.d - b.d));
    const rec = arr.length ? arr[0].rec : null;
    const cluster = arr.filter(x => x.d <= CLUSTER_RADIUS_PX).map(x => x.rec);
    return { rec, cluster: cluster.length ? cluster : (rec ? [rec] : []) };
}
function pickNearestRec(e) { return pickCluster(e).rec; }

let hoverPopup = null;
let hoverRec = null, hoverParam = null, hoverGeneration = -1;
let lastStationClickAt = 0;
function activateStationClick(e) {
    const { rec, cluster } = pickCluster(e);
    if (!rec) return false;
    lastStationClickAt = performance.now();
    hoverPopup.remove();
    hoverRec = null;
    // ≥2 stazioni sullo stesso punto (Comiso, Catania-Nicito…): invece di
    // aprire subito la scheda della sola stazione "sopra", le ESPLODO a
    // ventaglio (spiderfy) così sono tutte visibili e cliccabili singolarmente.
    if (cluster.length >= 2) {
        document.dispatchEvent(new CustomEvent('wsv2:station-cluster',
            { detail: { cluster, point: { x: e.point.x, y: e.point.y } } }));
    } else {
        document.dispatchEvent(new CustomEvent('wsv2:station-click', { detail: { rec, cluster } }));
    }
    return true;
}
function wireInteractions() {
    const map = state.map;
    hoverPopup = new maplibregl.Popup({
        closeButton: false, closeOnClick: false, offset: 10,
        className: 'wsv2-tooltip-shell', maxWidth: '260px',
    });
    const clearHover = () => {
        hoverRec = null;
        map.getCanvas().style.cursor = '';
        hoverPopup.remove();
    };
    map.on('movestart', clearHover);
    map.on('mousemove', [LYR], (e) => {
        if (map.isMoving()) return;
        const rec = pickNearestRec(e);
        if (!rec) return;
        map.getCanvas().style.cursor = 'pointer';
        if (hoverRec === rec && hoverParam === state.parametro && hoverGeneration === state._spriteGen) return;
        hoverRec = rec;
        hoverParam = state.parametro;
        hoverGeneration = state._spriteGen;
        hoverPopup.setLngLat([rec.lon, rec.lat]).setHTML(tooltipHtml(rec)).addTo(map);
    });
    map.on('mouseleave', [LYR], clearHover);
    map.on('click', [LYR], (e) => {
        activateStationClick(e);
    });
    // Hit-area di sicurezza: il box tecnico di un symbol MapLibre può essere
    // più piccolo del badge/etichetta disegnato. Un tap sul bordo sembrava non
    // funzionare. Cerchiamo quindi una stazione entro 18 px (28 su touch) e
    // riusiamo esattamente lo stesso picker/cluster del click diretto.
    map.on('click', (e) => {
        if (performance.now() - lastStationClickAt < 80 || !map.getLayer(LYR)) return;
        const pad = isMobile() ? 28 : 18;
        const box = [[e.point.x-pad,e.point.y-pad],[e.point.x+pad,e.point.y+pad]];
        const features = map.queryRenderedFeatures(box,{layers:[LYR]});
        if (!features.length) return;
        activateStationClick({...e,features});
    });
}

// --- API pubblica ------------------------------------------------------------
export function initStations(map, urlState) {
    state.map = map;
    state._pruneAtlas = createSpriteAtlasPruner(map, SRC, {
        images: state._images, limit: MAX_ATLAS_IMAGES,
        getUsed: () => state._usedSpriteKeys || new Set(),
        isSettled: () => state._appliedSpriteGen === state._spriteGen,
    });
    try { (window.wsv2 = window.wsv2 || {}).stations = state; } catch (e) {} // handle diagnostico
    state.requestedStationId = urlState?.station ? String(urlState.station) : '';
    // Il bot (screenshot) nasconde le stazioni a 0 di pioggia con ?hidezerorain=1.
    if (urlState?.raw?.get?.('hidezerorain') === '1') state.hideZeroRain = true;
    // Marker a dimensione forzata: solo per gli screenshot social del bot
    // (?markersize=full&markerscale=1.4). Assenti → comportamento automatico.
    const sizeMode = urlState?.raw?.get?.('markersize') || '';
    const sizeScale = urlState?.raw?.get?.('markerscale') || '';
    if (sizeMode || sizeScale) setMarkerSizeOverride({ mode: sizeMode, scale: sizeScale || 1 });
    ensureLayer();
    wireInteractions();
    map.on('zoomend', scheduleRender);
    map.on('moveend', scheduleRender);
    document.addEventListener('wsv2:viewchange',e=>{if(e.detail.map===map)render();});
    map.on('resize', scheduleRender);
    fetchData();
    fetchRapidStation();
    state._timer = setInterval(() => { if (!document.hidden) fetchData(); }, 60000);
    state._rapidTimer = setInterval(fetchRapidStation, 2500);
    if (!state._visibilityWired) {
        state._visibilityWired = true;
        // I tick saltati mentre la tab era nascosta non devono lasciare la
        // mappa ferma fino al prossimo minuto: al ritorno riallinea subito dati,
        // marker e scheda eventualmente aperta.
        document.addEventListener('visibilitychange', () => {
            if (!document.hidden) { fetchData(); fetchRapidStation(); }
        });
        window.addEventListener('pageshow', (event) => {
            if (event.persisted && !document.hidden) { fetchData(); fetchRapidStation(); }
        });
    }
}

export function setParametro(parametro) {
    state.parametro = parametro;
    const match = /^rain(1|3|6|12|24|48|72|96|120)h$/.exec(parametro);
    if (match) {
        if (state._recentRain.hours !== Number(match[1])) {
            // Clearing only values left a fresh timestamp for the previous
            // period: returning to it could falsely reuse an empty cache.
            state._recentRain.values = new Map();
            state._recentRain.meta = null;
            state._recentRain.hours = 0;
            state._recentRain.at = 0;
        }
        refreshRecentRain(Number(match[1]));
    }
    rebuildRecords();
}

export function setOption(name, value) {
    state.options[name] = value;
    rebuildRecords();
}

export function setHideZeroRain(v) {
    state.hideZeroRain = !!v;
    rebuildRecords();
}
export function isRainAccumParam(parametro) {
    return RAIN_ACCUM_PARAMS.has(parametro);
}

export function setNetworks(networks) {
    // Accetta solo le reti presentate nel pannello. Le reti di riferimento
    // vengono aggiunte esclusivamente nella vista completa predefinita.
    state.networks = new Set((Array.isArray(networks) ? networks : [])
        .filter(network => SELECTABLE_NETWORKS.has(network)));
    fetchData();
}
